import { Button, CircularProgress, Divider, Paper, Stack, Tab, Tabs, Typography } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { generatePath } from 'react-router-dom';

import { API_SHARED_APP_LIST_APP_CONTENT_URL, API_SHARED_APP_LIST_APP_CONTENTS_URL } from '../../constants/apiUrls';
import { useConfig } from '../../hooks/useConfig';
import { AppListContent } from '../../hooks/useSharedAppLists';
import { useSnackBar } from '../../hooks/useSnackBar';
import { camelCaseKeys, snakeCaseKeys } from '../../util/object';
import { putRequestJSON, uploadFile } from '../../util/services';
import TextField from '../forms/TextField';
import Upload from '../forms/Upload';

interface AppListContentFormProps {
  sharedAppListId: string;
  content: AppListContent | null;
  onClose: () => void;
  targetingProfiles?: number[];
}

interface FormInputs {
  appIdentifier: string;
}

type OperationType = 'add' | 'remove';

function AppListContentForm({ sharedAppListId, content, onClose }: AppListContentFormProps) {
  const isEdit = !!content;
  const { showSnackBar } = useSnackBar();
  const { apiHost } = useConfig();
  const queryClient = useQueryClient();
  const [isUploadingCsv, setIsUploadingCsv] = useState(false);
  const [activeTab, setActiveTab] = useState<OperationType>('add');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const uploadCsvMutation = useMutation({
    mutationFn: ({ file, operation }: { file: File; operation: OperationType }) => uploadFile(
        apiHost,
        generatePath(
          API_SHARED_APP_LIST_APP_CONTENTS_URL, 
          { sharedAppListId }
        ),
        { operation },
        file
      ),
    onSuccess: (response, variables) => {
      queryClient.invalidateQueries({ queryKey: ['sharedAppListWithContents', sharedAppListId] });
      const action = variables.operation === 'add' ? 'added' : 'removed';
      
      if (variables.operation === 'remove' && response.removed !== undefined) {
        const message = `Successfully removed ${response.removed} app identifier(s)`;
        const skippedMessage = response.not_found > 0 
          ? `. ${response.not_found} identifier(s) were not found and skipped` 
          : '';
        showSnackBar(message + skippedMessage, 'success', {
          event: 'app_list_content_removed',
          shared_app_list_id: sharedAppListId,
          removed_count: response.removed,
          not_found_count: response.not_found,
        });
      } else {
        showSnackBar(`App identifiers ${action} successfully`, 'success', {
          event: variables.operation === 'add' ? 'app_list_content_added' : 'app_list_content_removed',
          shared_app_list_id: sharedAppListId,
        });
      }
      
      setSelectedFile(null);
      onClose();
    },
    onError: (error: Error) => {
      showSnackBar(`Error processing app identifiers: ${error.message}`, 'error', {
        event: 'app_list_content_save_error',
        shared_app_list_id: sharedAppListId,
      });
    }
  });

  const createCsvFromAppIdentifier = (appIdentifier: string): File => {
    const csvContent = `app_identifier\n${appIdentifier}`;
    const blob = new Blob([csvContent], { type: 'text/csv' });
    return new File([blob], 'app_identifier.csv', { type: 'text/csv' });
  };

  const updateAppListContent = useMutation({    
    mutationFn: ({ id, appIdentifier }: { id: number, appIdentifier: string }) => putRequestJSON(
        apiHost,
        generatePath(
          API_SHARED_APP_LIST_APP_CONTENT_URL, 
          { 
            sharedAppListId,
            id: id.toString() 
          }
        ),
        snakeCaseKeys({ appListContent: { appIdentifier } })
      ),
    onSuccess: (response) => {
      if (response.errors) {
        throw new Error(Array.isArray(response.errors) 
          ? response.errors.join(', ') 
          : response.errors);
      }
      
      queryClient.invalidateQueries({ queryKey: ['sharedAppListWithContents', sharedAppListId] });
    }
  });

  const form = useForm<FormInputs>({
    defaultValues: content ? camelCaseKeys(content) : {
      appIdentifier: ''
    }
  });

  const { handleSubmit } = form;

  const onSubmit = async (data: FormInputs) => {
    try {
      if (isEdit) {
        await updateAppListContent.mutateAsync({
          id: content.id,
          appIdentifier: data.appIdentifier
        });
        showSnackBar('App identifier updated successfully', 'success', {
          event: 'app_list_content_updated',
          shared_app_list_id: sharedAppListId,
          content_id: content.id,
        });
        onClose();
      } else {
        setIsUploadingCsv(true);
        const csvFile = createCsvFromAppIdentifier(data.appIdentifier);
        await uploadCsvMutation.mutateAsync({ file: csvFile, operation: activeTab });
      }
    } catch (error) {
      let action = 'adding';
      if (isEdit) {
        action = 'updating';
      } else if (activeTab === 'remove') {
        action = 'removing';
      }
      showSnackBar(`Error ${action} app identifier: ${error.message}`, 'error', {
        event: 'app_list_content_save_error',
        shared_app_list_id: sharedAppListId,
      });
    } finally {
      setIsUploadingCsv(false);
    }
  };

  const handleCsvSelect = (files: File[]) => {
    if (files.length === 0) {
      setSelectedFile(null);
      return;
    }
    setSelectedFile(files[0]);
  };

  const handleCsvUpload = async () => {
    if (!selectedFile) return;
    
    setIsUploadingCsv(true);
    
    try {
      await uploadCsvMutation.mutateAsync({ file: selectedFile, operation: activeTab });
    } finally {
      setIsUploadingCsv(false);
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: OperationType) => {
    setActiveTab(newValue);
    setSelectedFile(null);
    form.reset({ appIdentifier: '' });
  };

  const isLoading = updateAppListContent.isLoading || isUploadingCsv || uploadCsvMutation.isLoading;

  return (
    <Paper sx={{ p: 2 }}>
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <FormProvider {...form}>
        <Stack spacing={3}>
          <Typography variant="h6">
            {isEdit ? 'Edit App Identifier' : 'Edit List Contents'}
          </Typography>

          {!isEdit && (
            <Tabs value={activeTab} onChange={handleTabChange} aria-label="add or remove tabs">
              <Tab label="Add Identifiers" value="add" />
              <Tab label="Remove Identifiers" value="remove" />
            </Tabs>
          )}
          
          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack spacing={2}>
              <TextField
                name="appIdentifier"
                label="App Identifier"
                rules={{ 
                  required: 'App identifier is required',
                  pattern: {
                    value: /^[a-zA-Z0-9._-]+(\.[a-zA-Z0-9._-]+)*$/,
                    message: 'Please enter a valid app identifier format'
                  }
                }}
                fullWidth
                autoFocus
              />
            
              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button 
                  variant="outlined" 
                  onClick={onClose}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  variant="contained" 
                  disabled={isLoading}
                  endIcon={isLoading ? <CircularProgress size={16} /> : null}
                  color={isEdit || activeTab === 'add' ? 'primary' : 'error'}
                >
                  {(() => {
                    if (isEdit) return 'Update';
                    if (activeTab === 'add') return 'Add';
                    return 'Remove';
                  })()}
                </Button>
              </Stack>
            </Stack>
          </form>

          {!isEdit && (
            <Stack spacing={3}>
              <Divider>OR</Divider>
              
              <Typography variant="subtitle1">
                Upload CSV File with App Identifiers
              </Typography>
              
              <Typography variant="body2" color="text.secondary">
                {activeTab === 'add' 
                  ? 'Upload a CSV file with app identifiers to add multiple identifiers at once.'
                  : 'Upload a CSV file with app identifiers to remove multiple identifiers at once. Non-matching identifiers will be skipped.'}
              </Typography>

              <Upload
                name="csvFile"
                accept=".csv"
                label="Drag/Drop CSV File Here Or Click To Upload"
                onChange={handleCsvSelect}
                disabled={isLoading}
                rules={{
                  validate: {
                    fileType: (files) => {
                      if (!files || files.length === 0) return true;
                      const file = files[0];
                      return file.name.endsWith('.csv') || 'Please upload a CSV file';
                    }
                  }
                }}
                key={`csv-upload-${sharedAppListId}-${activeTab}`}
              />

              {selectedFile && (
                <Stack direction="row" spacing={2} justifyContent="flex-end">
                  <Button 
                    variant="outlined" 
                    onClick={() => setSelectedFile(null)}
                    disabled={isLoading}
                  >
                    Clear File
                  </Button>
                  <Button 
                    variant="contained" 
                    onClick={handleCsvUpload}
                    disabled={isLoading}
                    endIcon={isLoading ? <CircularProgress size={16} /> : null}
                    color={activeTab === 'add' ? 'primary' : 'error'}
                  >
                    {activeTab === 'add' ? 'Add' : 'Remove'}
                  </Button>
                </Stack>
              )}
            </Stack>
          )}
        </Stack>
      </FormProvider>
    </Paper>
  );
}

export default AppListContentForm; 